import { rewriteApiUrl, tokenKey } from '../_util/data-target';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AppVersionService } from './app-version.service';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface ErrorReportParams {
  message: string;
  error_type?: string;
  severity?: ErrorSeverity;
  status_code?: number;
  endpoint?: string;
  stack_trace?: string;
  extra?: Record<string, unknown>;
}

const ERROR_ENDPOINT = '/api/client-errors';

@Injectable({ providedIn: 'root' })
export class ErrorReportingService {
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly appVersion: AppVersionService,
  ) {}

  report(params: ErrorReportParams): void {
    // Fire-and-forget — never await, never throw
    void this.send(params);
  }

  private async send(params: ErrorReportParams): Promise<void> {
    try {
      let appVersionRelease = 'unknown';
      try {
        const v = await this.appVersion.getVersionInfo();
        appVersionRelease = v.release;
      } catch {
        // version lookup failed — not worth blocking the report
      }

      const body = {
        message: params.message,
        error_type: params.error_type ?? 'UnknownError',
        severity: params.severity ?? 'error',
        ...(params.status_code !== undefined ? { status_code: params.status_code } : {}),
        ...(params.endpoint ? { endpoint: params.endpoint } : {}),
        route: this.router.url ?? '/',
        ...(params.stack_trace ? { stack_trace: params.stack_trace } : {}),
        details_json: {
          app_version: appVersionRelease,
          online: navigator.onLine,
          browser: navigator.userAgent,
          ...params.extra,
        },
      };

      // Bounded timeout: error reporting fires frequently (e.g. once per failed sync
      // push) and must never leave a stalled connection open indefinitely when the
      // server is unreachable — that piles up across a browser's per-origin connection
      // limit and can starve other, legitimate requests.
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 10_000);
      try {
        await fetch(rewriteApiUrl(ERROR_ENDPOINT), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem(tokenKey()) ?? ''}`,
          },
          body: JSON.stringify(body),
          signal: abortController.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
    } catch {
      // Intentionally swallowed — error reporting must never cause cascading errors
    }
  }
}
